import React from 'react';
import { Capa, CapaStatus, CapaPriority, CapaType } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface CapaListTableProps {
  capas: Capa[];
  loading: boolean;
  filterStatus: 'ALL' | CapaStatus;
  filterPriority: 'ALL' | CapaPriority;
  filterType: 'ALL' | CapaType;
  onFilterStatusChange: (status: 'ALL' | CapaStatus) => void;
  onFilterPriorityChange: (priority: 'ALL' | CapaPriority) => void;
  onFilterTypeChange: (type: 'ALL' | CapaType) => void;
  onSelectCapa: (capa: Capa) => void;
  onOpenCreate: () => void;
}

export const CapaListTable: React.FC<CapaListTableProps> = ({
  capas,
  loading,
  filterStatus,
  filterPriority,
  filterType,
  onFilterStatusChange,
  onFilterPriorityChange,
  onFilterTypeChange,
  onSelectCapa,
  onOpenCreate,
}) => {
  const filtered = capas.filter((item) => {
    if (filterStatus !== 'ALL' && item.status !== filterStatus) return false;
    if (filterPriority !== 'ALL' && item.priority !== filterPriority) return false;
    if (filterType !== 'ALL' && item.capaType !== filterType) return false;
    return true;
  });

  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(filtered);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);

  const getStatusBadge = (status: CapaStatus) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'INITIATED':
      case 'UNDER_ASSESSMENT':
        return <span className="badge-pill badge-info">Initiated / Assessment</span>;
      case 'ROOT_CAUSE_ANALYSIS':
        return <span className="badge-pill badge-info">Root Cause Analysis</span>;
      case 'CAPA_PLANNING':
        return <span className="badge-pill badge-info">Planning</span>;
      case 'PENDING_APPROVAL':
        return <span className="badge-pill badge-warning">Pending Approval</span>;
      case 'APPROVED':
        return <span className="badge-pill badge-success">Approved</span>;
      case 'IMPLEMENTATION':
        return <span className="badge-pill badge-warning">Implementation</span>;
      case 'IMPLEMENTED':
      case 'PENDING_VERIFICATION':
      case 'VERIFICATION':
        return <span className="badge-pill badge-info">Verification</span>;
      case 'EFFECTIVENESS_MONITORING':
        return <span className="badge-pill badge-info">Effectiveness Monitoring</span>;
      case 'EFFECTIVE':
      case 'CLOSED':
        return <span className="badge-pill badge-success">Closed (Effective)</span>;
      case 'EXTENDED':
        return <span className="badge-pill badge-warning">Extended</span>;
      case 'REOPENED':
        return <span className="badge-pill badge-danger">Reopened</span>;
      case 'INEFFECTIVE':
      case 'REJECTED':
      case 'CANCELLED':
        return <span className="badge-pill badge-danger">{status}</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  const getPriorityBadge = (priority: CapaPriority) => {
    switch (priority) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'MEDIUM':
        return <span className="badge-pill badge-info">Medium</span>;
      case 'LOW':
        return <span className="badge-pill badge-neutral">Low</span>;
      default:
        return <span className="badge-pill badge-neutral">{priority}</span>;
    }
  };

  const isOverdue = (capa: Capa) => {
    if (capa.status === 'CLOSED' || capa.status === 'CANCELLED') return false;
    const due = new Date(capa.dueDate);
    const today = new Date();
    return due < today;
  };

  return (
    <div>
      {/* Table Toolbar */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
          {/* Status Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => onFilterStatusChange(e.target.value as any)}
              style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
            >
              <option value="ALL">All Statuses</option>
              <option value="INITIATED">Initiated</option>
              <option value="ROOT_CAUSE_ANALYSIS">Root Cause Analysis</option>
              <option value="CAPA_PLANNING">Planning</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="APPROVED">Approved</option>
              <option value="IMPLEMENTATION">Implementation</option>
              <option value="VERIFICATION">Verification</option>
              <option value="EFFECTIVENESS_MONITORING">Effectiveness Monitoring</option>
              <option value="CLOSED">Closed</option>
              <option value="EXTENDED">Extended</option>
              <option value="REOPENED">Reopened</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => onFilterPriorityChange(e.target.value as any)}
              style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
            >
              <option value="ALL">All Priorities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>CAPA Type</label>
            <select
              value={filterType}
              onChange={(e) => onFilterTypeChange(e.target.value as any)}
              style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
            >
              <option value="ALL">All Types</option>
              <option value="CORRECTIVE">Corrective</option>
              <option value="PREVENTIVE">Preventive</option>
              <option value="CORRECTIVE_AND_PREVENTIVE">Corrective & Preventive</option>
              <option value="SYSTEMIC">Systemic</option>
              <option value="REGULATORY">Regulatory</option>
              <option value="SAFETY">Safety</option>
              <option value="QUALITY">Quality</option>
            </select>
          </div>
        </div>

        <button
          onClick={onOpenCreate}
          className="btn-primary"
          style={{ width: 'auto', padding: '9px 16px' }}
        >
          + Initiate New CAPA
        </button>
      </div>

      {/* Table Container */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="capaNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                CAPA No & Title
              </SortableTh>
              <SortableTh field="capaType" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Type & Category
              </SortableTh>
              <SortableTh field="priority" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Priority
              </SortableTh>
              <SortableTh field="department" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Department & Owner
              </SortableTh>
              <SortableTh field="targetCompletionDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Due Date
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                  No CAPA records found.
                </td>
              </tr>
            ) : (
              paginatedItems.map((capa) => {
                const overdue = isOverdue(capa);
                return (
                  <tr
                    key={capa.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelectCapa(capa)}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#2563eb' }}>
                          {capa.capaNumber}
                        </span>
                        {capa.isSystemic && (
                          <span className="badge-pill badge-info">
                            Systemic
                          </span>
                        )}
                        {overdue && (
                          <span className="badge-pill badge-danger">
                            Overdue
                          </span>
                        )}
                      </div>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{capa.title}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{capa.problemStatement}</div>
                    </td>

                    <td>
                      <div style={{ fontWeight: 600, color: '#334155' }}>{capa.capaType}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{capa.category}</div>
                    </td>

                    <td>
                      {getPriorityBadge(capa.priority)}
                    </td>

                    <td>
                      <div style={{ fontWeight: 500, color: '#0f172a' }}>{capa.department}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{capa.ownerName}</div>
                    </td>

                    <td>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: overdue ? 700 : 500, color: overdue ? '#dc2626' : '#334155' }}>
                        {capa.dueDate ? capa.dueDate.substring(0, 10) : '-'}
                      </div>
                      {capa.authorityDueDate && (
                        <div style={{ fontSize: '10px', color: '#d97706' }}>
                          Authority: {capa.authorityDueDate.substring(0, 10)}
                        </div>
                      )}
                    </td>

                    <td>
                      {getStatusBadge(capa.status)}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectCapa(capa);
                        }}
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '12px' }}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="CAPAs"
      />
    </div>
  );
};
